// Consolidated utilities for Roles: permission parsing/normalization and validation
import type { PermItem, RoleFormData } from 'features/Admin/Roles/types/Roles.types';
import type { LayoutItem, WidgetData } from 'features/Dashboard/types/widgetTypes';

export interface RoleValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

export function validateRole(formData: RoleFormData): RoleValidationResult {
  const errors: Record<string, string> = {};
  const fd = formData as RoleFormData;
  const roleName = fd?.roleName;
  if (typeof roleName !== 'string' || roleName.trim() === '') {
    errors.roleName = 'Role name is required';
  }

  const permissions = fd.permissions;
  if (!Array.isArray(permissions)) {
    errors.permissions = 'Permissions must be an array';
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

export function normalizePermName(permName: string): string {
  return permName.replace(/\s/g, '').replace(/\//g, '-').replace(/:/g, '_');
}

/**
 * Whether the current permission selection satisfies a single widget-gating
 * permission key. Mirrors the widget-selector gating exactly so that the set of
 * widgets you can ADD always matches the set that survives a SAVE.
 *
 * `permissions` is the parallel boolean array to `permList` (one bool per entry).
 * Connection families (View/Create-Edit/Delete/Import...Connections) are
 * satisfied when ANY concrete connection permission of that family is enabled.
 */
export function isWidgetPermissionEnabled(
  permName: string,
  permList: PermItem[],
  permissions: boolean[],
): boolean {
  const list = permList || [];
  const hasConnectionPermission = (regex: RegExp) =>
    list.some((perm, index) => regex.test(normalizePermName(perm.name)) && permissions[index]);

  switch (permName) {
    case 'ViewConnections':
      return hasConnectionPermission(/^View[A-Za-z]+Connections$/);
    case 'Create-EditConnections':
      return hasConnectionPermission(/^Create-Edit[A-Za-z]+Connections$/);
    case 'DeleteConnections':
      return hasConnectionPermission(/^Delete[A-Za-z]+Connections$/);
    case 'ImportConnections':
      return hasConnectionPermission(/^Import[A-Za-z]+Connections$/);
    default: {
      const index = list.findIndex((perm) => normalizePermName(perm.name) === permName);
      return index >= 0 ? !!permissions[index] : false;
    }
  }
}

/**
 * A widget may be placed/kept only when EVERY permission it requires is enabled.
 * Unknown widget ids (not in `widgetData`) are treated as permitted so we never
 * silently drop data we don't understand.
 */
export function isWidgetPermitted(
  widgetId: number | string,
  widgetData: WidgetData | undefined,
  permList: PermItem[],
  permissions: boolean[],
): boolean {
  const widget = widgetData?.[String(widgetId)];
  if (!widget) return true;
  return (widget.permissions || []).every((permName) =>
    isWidgetPermissionEnabled(permName, permList, permissions),
  );
}

/**
 * Drop any layout widget whose gating permissions are no longer all satisfied.
 * Used so a role never persists (or displays) widgets it lacks permission for.
 * Returns the same array reference when nothing is removed so callers can cheaply
 * detect "no change" and avoid redundant state updates.
 */
export function filterLayoutByPermissions(
  layout: LayoutItem[],
  widgetData: WidgetData | undefined,
  permList: PermItem[],
  permissions: boolean[],
): LayoutItem[] {
  if (!Array.isArray(layout) || !widgetData) return Array.isArray(layout) ? layout : [];
  const filtered = layout.filter((item) =>
    isWidgetPermitted(item.id, widgetData, permList, permissions),
  );
  return filtered.length === layout.length ? layout : filtered;
}

export function parsePermName(permName: string): string {
  if (!permName || typeof permName !== 'string') return '';

  let str = permName.replace(/-/g, '/').replace(/_/g, ':');

  // Use title case to find word boundaries without splitting acronym letters.
  // The output below keeps the original capitalization for permission-key matching.
  const characters = str
    .replace(/USGS|FEMA|IPAWS/g, (acronym) => acronym[0] + acronym.slice(1).toLowerCase())
    .split('');
  const result: string[] = [];

  for (let index = 0; index < characters.length; index++) {
    const char = characters[index];
    if (
      char === char.toUpperCase() &&
      index > 0 &&
      !(char === 'I' && characters[index - 1] === 'A') &&
      // RescAlert
      !(char === 'A' && characters[index - 1] === 'c') &&
      // AWARE
      !(char === 'W' && characters[index - 1] === 'A') &&
      !(char === 'A' && characters[index - 1] === 'W') &&
      !(char === 'R' && characters[index - 1] === 'A') &&
      !(char === 'E' && characters[index - 1] === 'R') &&
      // VMS
      !(char === 'M' && characters[index - 1] === 'V') &&
      !(char === 'S' && characters[index - 1] === 'M')
    ) {
      result.push(' ');
    }
    result.push(str[index]);
  }

  str = result.join('');

  str = str.replace(' : ', ': ').replace('Siren / Giant', 'Siren/Giant');
  return str;
}
